import { useState, useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} from "../../store/slices/apiSlice";
import { useToast } from "./use-toast";
import { Company, CreateCompanyData } from "../../store/types";
import { getErrorMessage } from "./use-getError";

export const useCompaniesAPI = () => {
  const companiesEP = useAppSelector((state) => state.endpoints.companies);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companiesDropdownOpts, setCompaniesDropdownOpts] = useState<
    { label: string; value: any }[]
  >([]);
  const { toastRef, showSuccess, showError } = useToast();

  const [addItem, { isLoading: isAdding }] = useAddMutation();
  const [editItem, { isLoading: isEditing }] = useUpdateMutation();
  const [deleteItem, { isLoading: isDeleting }] = useRemoveMutation();

  const { data: fetchedCompanies, refetch: refetchCompanies } = useGetQuery(
    companiesEP,
    {
      refetchOnMountOrArgChange: true,
      skip: !companiesEP,
    }
  );

  const refetchData = async () => {
    await refetchCompanies();
  };

  const handleError = (operation: string, error: any) => {
    showError(
      "Ошибка",
      `Не удалось ${operation}, ошибка: ${error}. Попробуйте позже.`
    );
  };

  const handlePostCompany = async (
    company: CreateCompanyData
  ): Promise<boolean> => {
    try {
      const result = await addItem({
        endpoint: companiesEP,
        id: "",
        body: company,
      });
      if (result.data) {
        showSuccess("Успешно", "Компания создана");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("создать компанию", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("создать компанию", error);
      return false;
    }
  };

  const handleUpdateCompany = async (company: Company): Promise<boolean> => {
    try {
      const result = await editItem({
        endpoint: companiesEP,
        id: company?.id,
        body: company,
      });
      if (result.data) {
        showSuccess("Успешно", "Данные компании обновлены");
        return true;
      }

      let serverError;
      if (result.error) {
        serverError = getErrorMessage(result.error);
      }
      handleError("обновить компанию", serverError ?? result.error);
      return false;
    } catch (error) {
      handleError("обновить компанию", error);
      return false;
    }
  };

  const handleDeleteCompany = async (company: Company): Promise<boolean> => {
    try {
      const result = await deleteItem({
        endpoint: companiesEP,
        id: company?.id,
      });
      if (result.error) {
        let serverError = getErrorMessage(result.error);
        handleError("удалить компанию", serverError ?? result.error);
        return false;
      }

      showSuccess("Успешно", "Компания удалена");
      return true;
    } catch (error) {
      handleError("удалить компанию", error);
      return false;
    }
  };

  useEffect(() => {
    if (fetchedCompanies) setCompanies(fetchedCompanies);
  }, [fetchedCompanies]);

  useEffect(() => {
    if (companies) {
      const formatted = companies.map((organization) => ({
        label: organization.name,
        value: organization.id,
      }));
      setCompaniesDropdownOpts(formatted);
    } else {
      setCompaniesDropdownOpts([]);
    }
  }, [companies]);

  return {
    companiesEP,
    handlePostCompany,
    handleUpdateCompany,
    handleDeleteCompany,
    refetchData,
    companies,
    companiesDropdownOpts,
    isAdding,
    isEditing,
    isDeleting,
    toastRef,
  };
};
